/* ═══════════════════════════════════════════════════════════════
   ÈLAN CHAPTER 4 — The Eid Finale
   JS Controller  ·  GSAP ScrollTrigger (lazy-loaded)
   + IntersectionObserver for standard reveals
   Golden curtain hero  ·  Runway horizontal gallery
   Word-split  ·  Particles  ·  Cinema parallax  ·  Marquee
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  if (!document.body.classList.contains('elan-ch4-page')) return;

  var ANIM_SELECTORS = '.ec4-reveal, .ec4-clip-up, .ec4-scale-in, .ec4-fade, .ec4-slide-left, .ec4-slide-right, .ec4-mask-up, .ec4-line-grow, .ec4-draw-in, .ec4-mosaic__item--depth';
  var _observer = null;
  var _gsapLoaded = false;
  var _gsapCallbacks = [];

  /* ─────────────────────────────────────────────────────────────
     §0  GSAP Loader — Lazy-load from CDN
     ───────────────────────────────────────────────────────────── */
  function loadGSAP(callback) {
    if (_gsapLoaded) { callback(); return; }
    _gsapCallbacks.push(callback);
    if (_gsapCallbacks.length > 1) return; /* already loading */

    var gsapUrl = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    var stUrl   = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

    function loadScript(url, cb) {
      var s = document.createElement('script');
      s.src = url; s.async = true;
      s.onload = cb;
      s.onerror = function () { console.warn('[EC4] Failed to load:', url); cb(); };
      document.head.appendChild(s);
    }

    loadScript(gsapUrl, function () {
      loadScript(stUrl, function () {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
          _gsapLoaded = true;
        }
        _gsapCallbacks.forEach(function (fn) { fn(); });
        _gsapCallbacks = [];
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §1  Scroll-reveal (IntersectionObserver)
     ───────────────────────────────────────────────────────────── */
  function initObserver() {
    if (_observer) _observer.disconnect();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      showAll();
      return;
    }

    _observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            _observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -30px 0px' }
    );

    document.querySelectorAll(ANIM_SELECTORS).forEach(function (el) {
      if (!el.classList.contains('is-visible')) _observer.observe(el);
    });

    document.querySelectorAll('.ec4-split__media').forEach(function (el) {
      if (!el.classList.contains('is-visible')) _observer.observe(el);
    });

    /* Above-fold safety fallback */
    setTimeout(function () {
      document.querySelectorAll('.ec4-clip-up, .ec4-split__media, .ec4-mosaic__item--depth').forEach(function (el) {
        var rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight && !el.classList.contains('is-visible')) {
          el.classList.add('is-visible');
        }
      });
    }, 800);
  }

  function showAll() {
    document.querySelectorAll(ANIM_SELECTORS).forEach(function (el) {
      el.classList.add('is-visible');
    });
    document.querySelectorAll('.ec4-split__media').forEach(function (el) {
      el.classList.add('is-visible');
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §2  Stagger index
     ───────────────────────────────────────────────────────────── */
  function initStagger() {
    document.querySelectorAll('.ec4-stagger').forEach(function (grid) {
      Array.prototype.forEach.call(grid.children, function (child, i) {
        child.style.setProperty('--ec4-delay', String(i));
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §3  Hero word-split
     ───────────────────────────────────────────────────────────── */
  function initWordSplit() {
    var headings = document.querySelectorAll('.ec4-hero__heading[data-ec4-split]');
    headings.forEach(function (h) {
      if (h.dataset.ec4SplitDone) return;
      var text = h.textContent.trim();
      var words = text.split(/\s+/);
      var html = '';
      for (var i = 0; i < words.length; i++) {
        var delay = 1.6 + (i * 0.14); /* start after curtain reveal */
        html += '<span class="ec4-word" style="animation-delay:' + delay.toFixed(2) + 's">' + words[i] + '</span>';
        if (i < words.length - 1) html += ' ';
      }
      h.innerHTML = html;
      h.dataset.ec4SplitDone = '1';
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §4  Golden curtain reveal — peels away on page load
     ───────────────────────────────────────────────────────────── */
  function initCurtainReveal() {
    var curtain = document.querySelector('.ec4-hero__curtain');
    if (!curtain) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      curtain.style.display = 'none';
      return;
    }
    /* Trigger reveal after a brief delay */
    setTimeout(function () {
      curtain.classList.add('ec4-curtain--revealed');
    }, 300);
    /* Remove from DOM after animation to avoid z-index issues */
    curtain.addEventListener('animationend', function () {
      curtain.style.display = 'none';
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §5  Golden particles — canvas effect in hero
     ───────────────────────────────────────────────────────────── */
  function initParticles() {
    var container = document.querySelector('.ec4-hero__particles');
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var canvas = document.createElement('canvas');
    canvas.style.cssText = 'width:100%;height:100%;display:block;';
    container.appendChild(canvas);

    var ctx = canvas.getContext('2d');
    var particles = [];
    var PARTICLE_COUNT = Math.min(40, Math.floor(window.innerWidth / 35));
    var raf;

    function resize() {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    }

    function createParticle() {
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 0.5,
        speedX: (Math.random() - 0.5) * 0.25,
        speedY: -Math.random() * 0.4 - 0.08,
        opacity: Math.random() * 0.5 + 0.15,
        fadeDir: Math.random() > 0.5 ? 1 : -1,
        hue: 42 + Math.random() * 12 /* richer gold range */
      };
    }

    function init() {
      resize();
      particles = [];
      for (var i = 0; i < PARTICLE_COUNT; i++) {
        particles.push(createParticle());
      }
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (var i = 0; i < particles.length; i++) {
        var p = particles[i];
        p.x += p.speedX;
        p.y += p.speedY;
        p.opacity += p.fadeDir * 0.003;

        if (p.opacity <= 0.05 || p.opacity >= 0.6) p.fadeDir *= -1;

        if (p.y < -10 || p.x < -10 || p.x > canvas.width + 10) {
          particles[i] = createParticle();
          particles[i].y = canvas.height + 10;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = 'hsla(' + p.hue + ', 65%, 55%, ' + p.opacity + ')';
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    }

    init();
    draw();

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 200);
    });

    /* Pause when hero not visible */
    var heroObs = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        if (!raf) draw();
      } else {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
      }
    }, { threshold: 0.05 });
    heroObs.observe(container.parentElement);
  }

  /* ─────────────────────────────────────────────────────────────
     §6  Runway — GSAP ScrollTrigger horizontal scroll
     The showstopper: pinned horizontal gallery
     ───────────────────────────────────────────────────────────── */
  function initRunway() {
    var section = document.querySelector('.ec4-runway');
    if (!section) return;
    if (window.innerWidth < 768) return; /* mobile uses native scroll-snap */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    loadGSAP(function () {
      if (!window.gsap || !window.ScrollTrigger) return;

      var viewport = section.querySelector('.ec4-runway__viewport');
      var track = section.querySelector('.ec4-runway__track');
      var frames = section.querySelectorAll('.ec4-runway__frame');
      var progressBar = section.querySelector('.ec4-runway__progress-bar');

      if (!viewport || !track || frames.length === 0) return;

      var currentST = null;

      function buildRunwayScroll() {
        /* Kill previous instance to allow rebuild */
        if (currentST) { currentST.kill(); currentST = null; }
        /* Reset track transform */
        window.gsap.set(track, { clearProps: 'x' });

        /* scrollWidth accounts for children, gaps, and padding */
        var scrollDistance = track.scrollWidth - viewport.offsetWidth;
        if (scrollDistance <= 0) return;

        currentST = window.ScrollTrigger.create({
          trigger: viewport,
          pin: true,
          scrub: 1,
          start: 'top top',
          end: '+=' + scrollDistance,
          invalidateOnRefresh: true,
          animation: window.gsap.to(track, {
            x: -scrollDistance,
            ease: 'none'
          }),
          onUpdate: function (self) {
            /* Update progress bar */
            if (progressBar) {
              progressBar.style.width = (self.progress * 100) + '%';
            }
            /* Activate centered frame's caption */
            var centerX = viewport.offsetWidth / 2;
            frames.forEach(function (f) {
              var rect = f.getBoundingClientRect();
              var viewRect = viewport.getBoundingClientRect();
              var relLeft = rect.left - viewRect.left;
              var relRight = relLeft + rect.width;
              if (relLeft < centerX && relRight > centerX) {
                f.classList.add('is-active');
              } else {
                f.classList.remove('is-active');
              }
            });
          }
        });
      }

      /* Build initial animation */
      buildRunwayScroll();

      /* Rebuild after images fully load (dimensions may change with auto-width) */
      var images = section.querySelectorAll('.ec4-runway__frame img');
      var loaded = 0;
      var total = images.length;

      function onImageLoad() {
        loaded++;
        if (loaded >= total) {
          buildRunwayScroll();
          window.ScrollTrigger.refresh();
        }
      }

      if (total > 0) {
        images.forEach(function (img) {
          if (img.complete && img.naturalWidth > 0) {
            loaded++;
          } else {
            img.addEventListener('load', onImageLoad);
            img.addEventListener('error', onImageLoad);
          }
        });
        if (loaded >= total) {
          /* All already loaded — just refresh */
          window.ScrollTrigger.refresh();
        }
      }

      /* Safety net: rebuild after 3s in case load events were missed */
      setTimeout(function () {
        buildRunwayScroll();
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }, 3000);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §7  Cinema parallax
     ───────────────────────────────────────────────────────────── */
  function initCinemaParallax() {
    var cinemas = document.querySelectorAll('.ec4-cinema__media--parallax');
    if (!cinemas.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        cinemas.forEach(function (media) {
          var rect = media.getBoundingClientRect();
          var viewH = window.innerHeight;
          if (rect.top < viewH && rect.bottom > 0) {
            var progress = (viewH - rect.top) / (viewH + rect.height);
            var translateY = (progress - 0.5) * -30;
            var child = media.querySelector('video, img');
            if (child) {
              child.style.transform = 'scale(1.15) translateY(' + translateY + 'px)';
            }
          }
        });
        ticking = false;
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────────────────────────────────────────────────────────
     §8  Marquee — duplicate content for seamless loop
     ───────────────────────────────────────────────────────────── */
  function initMarquee() {
    document.querySelectorAll('.ec4-marquee__track').forEach(function (track) {
      if (track.dataset.ec4MarqueeDone) return;
      var children = Array.prototype.slice.call(track.children);
      children.forEach(function (child) {
        var clone = child.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      });
      track.dataset.ec4MarqueeDone = '1';
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §9  Video autoplay
     ───────────────────────────────────────────────────────────── */
  function initVideos() {
    var videos = document.querySelectorAll('[data-ec4-video]');
    if (!videos.length) return;

    var vidObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            if (v.readyState < 2) v.load();
            v.play().catch(function () { /* autoplay blocked */ });
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.05, rootMargin: '200px 0px 200px 0px' }
    );
    videos.forEach(function (v) {
      v.setAttribute('preload', 'auto');
      vidObserver.observe(v);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     §10  Cinema letterbox — add class when in viewport
     ───────────────────────────────────────────────────────────── */
  function initLetterbox() {
    var cinemas = document.querySelectorAll('.ec4-cinema[data-letterbox="true"]');
    if (!cinemas.length) return;

    var lbObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('has-letterbox');
        }
      });
    }, { threshold: 0.2 });

    cinemas.forEach(function (c) { lbObs.observe(c); });
  }

  /* ─────────────────────────────────────────────────────────────
     §11  Init
     ───────────────────────────────────────────────────────────── */
  function init() {
    initCurtainReveal();
    initWordSplit();
    initStagger();
    initObserver();
    initVideos();
    initParticles();
    initRunway();
    initCinemaParallax();
    initMarquee();
    initLetterbox();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor lifecycle */
  document.addEventListener('shopify:section:load', function () {
    initCurtainReveal();
    initWordSplit();
    initStagger();
    initObserver();
    initVideos();
    initMarquee();
    initLetterbox();
    /* Re-init runway after a brief delay to ensure DOM is ready */
    setTimeout(initRunway, 300);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('.ec4-cta')) {
      document.body.classList.remove('elan-ch4-page');
      if (_observer) { _observer.disconnect(); _observer = null; }
      /* Kill GSAP ScrollTrigger instances */
      if (window.ScrollTrigger) {
        window.ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
      }
    }
  });
})();
